/**
 * Vitolier — Locale resolution
 * -----------------------------
 * Centralised priority chain used to decide which locale to land an
 * unprefixed visitor on. Higher priority wins:
 *
 *   1. Explicit user choice persisted in localStorage (set via the
 *      language switcher). Once set, geo never overrides it.
 *   2. Authenticated user profile preferred_language (when provided).
 *   3. Geo-IP country (sync cache, or async edge function).
 *   4. Browser navigator.language(s).
 *   5. English fallback.
 *
 * Note: LocaleContext also auto-persists the active locale segment so
 * users who arrive on /fr keep getting French. The separate "explicit"
 * flag lets us distinguish a user-chosen locale from one that was
 * simply baked into a URL via earlier geo redirect.
 */

import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from "@/contexts/LocaleContext";
import { detectGeoCountry, getDetectedGeoCountry } from "@/lib/compliance/geo-ip";

const LS_LOCALE = "vitolier_locale";
const LS_EXPLICIT = "vitolier_locale_explicit";

function isLocale(v: string | null | undefined): v is Locale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

/** Map ISO-3166-1 alpha-2 country code → preferred supported locale. */
const COUNTRY_TO_LOCALE: Record<string, Locale> = {
  // English-speaking
  UK: "en", GB: "en", US: "en", CA: "en", AU: "en", IE: "en", NZ: "en",
  ZA: "en", IN: "en", SG: "en", PH: "en",
  // German-speaking (CH defaults to German — largest linguistic community)
  DE: "de", AT: "de", LI: "de", CH: "de",
  // French-speaking (BE/LU default to French; CH covered above by DE)
  FR: "fr", BE: "fr", LU: "fr", MC: "fr",
  CI: "fr", SN: "fr", MA: "fr", DZ: "fr", TN: "fr", CM: "fr",
  // Spanish-speaking
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es",
  UY: "es", PY: "es", BO: "es", EC: "es", CR: "es", PA: "es", DO: "es",
  GT: "es", HN: "es", SV: "es", NI: "es", CU: "es", PR: "es",
  // Portuguese-speaking
  PT: "pt", BR: "pt", AO: "pt", MZ: "pt", CV: "pt", GW: "pt", ST: "pt", TL: "pt",
  // Dutch-speaking
  NL: "nl", AW: "nl", CW: "nl", SR: "nl",
  // Italian-speaking
  IT: "it", SM: "it", VA: "it",
  // Japanese
  JP: "ja",
  // Korean
  KR: "ko",
  // Simplified Chinese (mainland)
  CN: "zh",
  // Traditional Chinese
  TW: "zh-tw", HK: "zh-tw", MO: "zh-tw",
};

export function localeFromCountry(country: string | null | undefined): Locale | null {
  if (!country) return null;
  const code = country.trim().toUpperCase();
  return COUNTRY_TO_LOCALE[code] ?? null;
}

/** Parse a BCP-47 language tag and match against supported locales. */
export function localeFromBrowserTag(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  // Exact: zh-tw, zh-hk, en-gb …
  if (isLocale(lower)) return lower;
  // Traditional Chinese regions
  if (/^zh-(tw|hk|mo|hant)/.test(lower)) return "zh-tw";
  // Simplified Chinese
  if (/^zh(-|$)/.test(lower)) return "zh";
  // Base language
  const base = lower.split("-")[0];
  if (isLocale(base)) return base;
  return null;
}

function localeFromBrowser(): Locale | null {
  if (typeof navigator === "undefined") return null;
  const list: string[] = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? [...navigator.languages]
    : navigator.language
    ? [navigator.language]
    : [];
  for (const tag of list) {
    const match = localeFromBrowserTag(tag);
    if (match) return match;
  }
  return null;
}

/** Has the user explicitly chosen a locale (language switcher)? */
export function hasExplicitLocaleChoice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(LS_EXPLICIT) === "1"
      && isLocale(window.localStorage.getItem(LS_LOCALE));
  } catch {
    return false;
  }
}

/** Mark the current stored locale as user-chosen. */
export function markLocaleExplicit(locale: Locale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_LOCALE, locale);
    window.localStorage.setItem(LS_EXPLICIT, "1");
  } catch {
    // ignore
  }
}

function readStoredLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(LS_LOCALE);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

interface ResolveOptions {
  /** Authenticated profile preferred language, if known. */
  profileLanguage?: string | null;
}

/**
 * Synchronous resolver — uses only signals already available without
 * network. Returns DEFAULT_LOCALE as last resort.
 *
 * Priority: explicit choice > profile language > cached geo > browser > en
 */
export function resolveLocaleSync(opts: ResolveOptions = {}): Locale {
  // 1. Explicit user choice
  if (hasExplicitLocaleChoice()) {
    const stored = readStoredLocale();
    if (stored) return stored;
  }

  // 2. Authenticated profile language
  const fromProfile = localeFromBrowserTag(opts.profileLanguage ?? null);
  if (fromProfile) return fromProfile;

  // 3. Geo (cached / edge-injected only — sync)
  const fromGeo = localeFromCountry(getDetectedGeoCountry());
  if (fromGeo) return fromGeo;

  // 4. Browser language
  const fromBrowser = localeFromBrowser();
  if (fromBrowser) return fromBrowser;

  // 5. Fallback
  return DEFAULT_LOCALE;
}

/**
 * Async resolver — same priority chain but will trigger a one-shot
 * geo-IP edge fetch when no cached signal is present. Bounded by the
 * geo edge function's own 2.5s timeout.
 */
export async function resolveLocaleAsync(opts: ResolveOptions = {}): Promise<Locale> {
  // Short-circuit when an explicit/profile signal already wins.
  if (hasExplicitLocaleChoice()) {
    const stored = readStoredLocale();
    if (stored) return stored;
  }
  const fromProfile = localeFromBrowserTag(opts.profileLanguage ?? null);
  if (fromProfile) return fromProfile;

  const cachedGeo = localeFromCountry(getDetectedGeoCountry());
  if (cachedGeo) return cachedGeo;

  try {
    const geo = await detectGeoCountry();
    const fromEdge = localeFromCountry(geo.code);
    if (fromEdge) return fromEdge;
  } catch {
    // ignore — fall through to browser/en
  }

  const fromBrowser = localeFromBrowser();
  if (fromBrowser) return fromBrowser;

  return DEFAULT_LOCALE;
}
